/**
 * Timeline of one outing.
 *
 * The server already projected the plan into segments (activity, travel,
 * buffer), so this component only has to render them in clock order and add the
 * two ends of the day: the event start and the end the plan actually reaches,
 * which is the end of the last segment — a plan without segments ends where it
 * starts. The organizer's declared end travels beside it when the two differ,
 * because a plan can run past the declared time and the two numbers mean
 * different things.
 */

import { useTranslation } from 'react-i18next'

import type { CalendarTimelineEntryDto } from '@shared/types'

import { cn, formatLocalDateTime } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'

export interface TimelineProps {
  /** First moment of the plan, the event's own start. */
  startAt: string
  /** Explicit end set by the organizer, when there is one. */
  endAt?: string | null
  /** Projected segments, oldest first. */
  entries: CalendarTimelineEntryDto[]
  className?: string
}

/** `HH:mm` of a naive local date-time. */
function clockTime(local: string): string {
  return local.slice(11, 16)
}

export function Timeline({ startAt, endAt, entries, className }: TimelineProps) {
  const { t } = useTranslation()
  const locale = currentLocale()

  // Sorting by the local start keeps the render order equal to the wall clock
  // even if a payload ever arrives out of order.
  const ordered = [...entries].sort((left, right) => left.startAt.localeCompare(right.startAt))
  // "Expected end" is what the plan reaches. The organizer's declared end is a
  // separate fact and is only repeated when it differs, so the footer never shows a
  // contradiction of the overrun notice above it.
  const plannedEnd = ordered[ordered.length - 1]?.endAt ?? startAt
  const declaredEnd = endAt ?? null

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b py-2">
        <span className="text-xs font-medium">{t('calendar.startAt')}</span>
        <span className="text-sm">{formatLocalDateTime(startAt, locale)}</span>
      </div>

      <ol className="divide-border flex flex-col divide-y">
        {ordered.map((entry) => (
          <li
            key={entry.key}
            className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className={cn(
                  'size-2 rounded-full',
                  entry.kind === 'activity' ? 'bg-primary' : 'bg-muted-foreground/40',
                )}
              />
              <span
                className={cn(
                  'text-sm',
                  entry.kind === 'activity' ? 'font-medium' : 'text-muted-foreground',
                )}
              >
                {t(entry.labelKey)}
              </span>
            </div>

            <div className="text-muted-foreground flex flex-wrap gap-x-3 text-xs">
              <span>
                {clockTime(entry.startAt)} – {clockTime(entry.endAt)}
              </span>
              <span>{t('calendar.duration', { minutes: entry.durationMinutes })}</span>
            </div>
          </li>
        ))}
      </ol>

      <div className="border-border flex flex-wrap items-center justify-between gap-2 border-t py-2">
        <span className="text-xs font-medium">{t('calendar.endAt')}</span>
        <span className="text-sm">{formatLocalDateTime(plannedEnd, locale)}</span>
      </div>

      {declaredEnd !== null && declaredEnd !== plannedEnd ? (
        <div className="border-border text-muted-foreground flex flex-wrap items-center justify-between gap-2 border-t py-2">
          <span className="text-xs font-medium">{t('calendar.declaredEnd')}</span>
          <span className="text-sm">{formatLocalDateTime(declaredEnd, locale)}</span>
        </div>
      ) : null}
    </div>
  )
}
